import { useState, useEffect } from "react";
import {
  Label,
  Button,
  Skeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
} from "@pushpress/pushpress-ui";
import { CheckIcon, XIcon, Pencil } from "lucide-react";
import ComboboxSelect from "./ComboboxSelect";
import { REFERRED_BY_OPTIONS, ReferredByOptionId } from "../../constants";
import {
  getReferredByUserOptions,
  getReferredBySelectOption,
} from "../../helpers";
import type { MemberUserType } from "../../types";
import type { StaffRecordType } from "../../../People/types";

type EditableReferredBySelectFieldProps = {
  label: string;
  referredById?: string | null;
  referredByOther?: string | null;
  referredByUserId?: string | null;
  onSave: (
    referredById: string,
    referredByOther: string,
    referredByUserId: string,
  ) => void;
  hasData?: boolean;
  members?: MemberUserType[];
  staff?: StaffRecordType[];
};

export default function EditableReferredBySelectField({
  label,
  referredById = ReferredByOptionId.Unassigned, // 0 is the "unassigned" value in DB
  referredByOther = "",
  referredByUserId = ReferredByOptionId.Unassigned, // 0 is the "unassigned" value in DB
  onSave,
  hasData = false,
  members,
  staff,
}: EditableReferredBySelectFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editReferredById, setEditReferredById] = useState(referredById);
  const [editReferredByOther, setEditReferredByOther] =
    useState(referredByOther);
  const [editReferredByUserId, setEditReferredByUserId] =
    useState(referredByUserId);

  const isAdvertisementOrOther = (referredById: string) => {
    return (
      referredById === ReferredByOptionId.Advertisement ||
      referredById === ReferredByOptionId.Other
    );
  };

  const isExistingMember = (referredById: string) => {
    return referredById === ReferredByOptionId.ExistingMember;
  };

  const isStaffMember = (referredById: string) => {
    return referredById === ReferredByOptionId.StaffMember;
  };

  const showOtherInput = isAdvertisementOrOther(editReferredById);
  const showMemberSelect = isExistingMember(editReferredById);
  const showStaffSelect = isStaffMember(editReferredById);

  const selectedOption = getReferredBySelectOption(
    referredById,
    referredByOther,
    referredByUserId,
    members || [],
    staff || [],
  );

  const handleSave = () => {
    onSave(editReferredById, editReferredByOther, editReferredByUserId);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditReferredById(referredById);
    setEditReferredByOther(referredByOther);
    setEditReferredByUserId(referredByUserId);
    setIsEditing(false);
  };

  useEffect(() => {
    setEditReferredById(referredById);
    setEditReferredByOther(referredByOther);
    setEditReferredByUserId(referredByUserId);
  }, [referredById, referredByOther, referredByUserId]);

  const handleOptionChange = (value: string) => {
    setEditReferredById(value);
    setEditReferredByOther("");
    setEditReferredByUserId("0");
  };

  if (!hasData) {
    return (
      <div className="tw-group">
        <Label className="tw-text-xs tw-text-neutral-600">{label}</Label>
        <Skeleton className="tw-h-4 tw-w-1/2" />
      </div>
    );
  }

  return (
    <div className="tw-group tw-h-12">
      <Label className="tw-text-xs tw-text-neutral-600">{label}</Label>
      {isEditing ? (
        <div className="tw-flex tw-flex-col tw-gap-2">
          <div className="tw-flex tw-items-center">
            <Select
              value={editReferredById.toString()}
              onValueChange={(value) => handleOptionChange(value)}
            >
              <SelectTrigger className="tw-h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REFERRED_BY_OPTIONS().map((option) => (
                  <SelectItem
                    key={option.optionId}
                    value={option.optionId.toString()}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              data-testid="referred-by-save-button"
              variant="ghost"
              size="icon"
              className="tw-h-7 tw-w-7"
              onClick={handleSave}
            >
              <CheckIcon className="tw-h-4 tw-w-4 tw-text-success" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="tw-h-7 tw-w-7"
              onClick={handleCancel}
            >
              <XIcon className="tw-h-4 tw-w-4 tw-text-error" />
            </Button>
          </div>
          {showOtherInput && (
            <div className="tw-flex tw-flex-col tw-w-[84%]">
              <Label className="tw-text-sm tw-text-neutral-600">
                Please Specify
              </Label>
              <Input
                type="text"
                value={editReferredByOther}
                onChange={(e) => setEditReferredByOther(e.target.value)}
                className="tw-h-8"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSave();
                }}
              />
            </div>
          )}
          {showMemberSelect && (
            <div className="tw-flex tw-flex-col tw-w-[84%]">
              <Label className="tw-text-sm tw-text-neutral-600">
                Select Member
              </Label>
              <ComboboxSelect
                label="member"
                options={getReferredByUserOptions(members || [])}
                value={editReferredByUserId.toString()}
                onChange={(value) => setEditReferredByUserId(value)}
              />
            </div>
          )}
          {showStaffSelect && (
            <div className="tw-flex tw-flex-col tw-w-[84%]">
              <Label className="tw-text-sm tw-text-neutral-600">
                Select Staff
              </Label>
              <ComboboxSelect
                label="staff"
                options={getReferredByUserOptions(staff || [])}
                value={editReferredByUserId.toString()}
                onChange={(value) => setEditReferredByUserId(value)}
              />
            </div>
          )}
        </div>
      ) : (
        <div
          className="tw-cursor-pointer tw-py-1 hover:tw-bg-neutral-100 tw-transition-colors tw-group tw-flex tw-items-center"
          onClick={() => setIsEditing(true)}
          onKeyDown={() => setIsEditing(true)}
          role="button"
          tabIndex={0}
        >
          <p
            data-testid="referred-by-selected-field-view"
            className="tw-font-medium group-hover:tw-text-primary tw-transition-colors"
          >
            {selectedOption}
          </p>
          <div className="tw-ml-2 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
            <Pencil className="tw-h-4 tw-w-4 tw-text-neutral-600" />
          </div>
        </div>
      )}
    </div>
  );
}
